import { useState, useEffect, useContext } from "react";
import axios from "axios";
import { useDebouncedCallback } from "use-debounce";
import { FormikErrors, FormikProps, FormikTouched } from "formik";
import { AppCtx } from "src/contexts/app.context";
import { Checkout } from "src/organisms/CheckoutView/CheckoutStepInfo";
import { Profile } from "src/molecules/ProfileForm/ProfileForm";
import { Address } from "src/molecules/AddressForm/AddressForm";

type PostcodeAPIAddress = {
  street: string;
  streetNen: string;
  houseNumber: number;
  houseNumberAddition: string | null;
  postcode: string;
  city: string;
  cityShort: string;
  cityId: string;
  municipality: string;
  municipalityShort: string;
  municipalityId: string;
  province: string;
  rdX: number | null;
  rdY: number | null;
  latitude: number | null;
  longitude: number | null;
  bagNumberDesignationId: string | null;
  bagAddressableObjectId: string | null;
  addressType: string;
  purposes: string[] | null;
  surfaceArea: number | null;
  houseNumberAdditions: string[];
};

type PostcodeAPIResponse = {
  data: PostcodeAPIAddress;
};

const useAutompleteAddressNl = (
  formik: FormikProps<Checkout> | FormikProps<Profile>,
  formikValuesAddress: Address | undefined,
  formikErrorsAddress: FormikErrors<Address> | undefined,
  formikTouchedAddress: FormikTouched<Address> | undefined,
  addressType: "shippingAddress" | "billingAddress"
): boolean => {
  const { storeKey } = useContext(AppCtx);

  const formikValueAddressCountry = formikValuesAddress?.country;
  const formikValueAddressPostalCode = formikValuesAddress?.postalCode;
  const formikValueAddressStreetNumber = formikValuesAddress?.streetNumber;

  const formikErrorAddressPostalCode = formikErrorsAddress?.postalCode;
  const formikErrorAddressStreetNumber = formikErrorsAddress?.streetNumber;

  const formikTouchedAddressPostalCode = formikTouchedAddress?.postalCode;
  const formikTouchedAddressStreetNumber = formikTouchedAddress?.streetNumber;

  const [showAutocompleteAddressNlForm, setShowAutocompleteAddressNlForm] =
    useState(
      storeKey === "OT-COM" ||
        !formikValuesAddress ||
        (!!formikValueAddressPostalCode && !!formikValueAddressPostalCode)
    );

  const autocompleteAddressNl = useDebouncedCallback(async () => {
    let response: PostcodeAPIResponse;

    try {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      response = await axios(
        `/api/postcode/nl/v1/addresses/postcode/${formikValueAddressPostalCode}/${formikValueAddressStreetNumber}`
      );
    } catch (error) {
      // @ts-ignore
      console.error(error?.response?.data); // eslint-disable-line @typescript-eslint/no-unsafe-member-access
      return;
    } finally {
      setShowAutocompleteAddressNlForm(true);
    }

    const { data } = response;

    if (data) {
      await formik.setFieldValue(`${addressType}.city`, data.city);
      await formik.setFieldValue(`${addressType}.streetName`, data.street);
      await formik.setFieldValue(
        `${addressType}.additionalStreetInfo`,
        data.houseNumberAddition || ""
      );
    }
  }, 1000);

  useEffect(() => {
    if (
      formikValueAddressCountry === "NL" &&
      (formikTouchedAddressPostalCode || formikTouchedAddressStreetNumber) &&
      formikValueAddressPostalCode &&
      formikValueAddressStreetNumber &&
      !formikErrorAddressPostalCode &&
      !formikErrorAddressStreetNumber
    ) {
      void autocompleteAddressNl();
    }
  }, [
    autocompleteAddressNl,
    formikTouchedAddressPostalCode,
    formikTouchedAddressStreetNumber,
    formikValueAddressCountry,
    formikValueAddressPostalCode,
    formikValueAddressStreetNumber,
    formikErrorAddressPostalCode,
    formikErrorAddressStreetNumber,
  ]);

  return showAutocompleteAddressNlForm;
};

export { useAutompleteAddressNl };
